import { BreadcrumbJsonLd, ProductJsonLd } from 'next-seo';
import PropTypes from 'prop-types';

import { getOfferImageLink } from 'lib/image';
import { toAbsoluteLink, toCategoryLink, toOfferLink } from 'lib/links';
import { toFormattedOfferPrice } from 'lib/offerPage';
import placeholder from 'lib/placeholder';
import {
  appendDynamicSubcategoryToBreadcrumbSchema,
  toReviewSchema,
} from 'lib/schemas';
import { toFormattedJsonLdString } from 'lib/string';

import APP from 'constants/app';
import OFFER from 'constants/offer';

const getRangePrices = (offers) => {
  let lowestPrice = null;
  let highestPrice = null;

  for (const offer of offers) {
    if (!lowestPrice || offer.price < lowestPrice.price) {
      lowestPrice = {
        price: offer.price,
        priceType: offer.priceType,
      };
    }

    if (!highestPrice || offer.price > highestPrice.price) {
      highestPrice = {
        price: offer.price,
        priceType: offer.priceType,
      };
    }
  }

  return [lowestPrice, highestPrice];
};

const Schema = ({
  categoryName = null,
  categorySlug,
  offerId = null,
  offerInstructionsRaw = null,
  offerPhoto = null,
  offerPrice = null,
  offerPriceType,
  offerSlug = null,
  offerTitle = null,
  reviewRating = null,
  reviews = [],
  storeDomain = null,
  storeName = null,
  offersToCompare = [],
  subcategoryName = null,
  subcategorySlug = null,
  totalReviews = null,
}) => {
  const offerCount = offersToCompare.length + 1;
  const [lowestPriceOffer, highestPriceOffer] = getRangePrices([
    ...offersToCompare,
    { priceType: offerPriceType, price: offerPrice },
  ]);

  return (
    <>
      <BreadcrumbJsonLd
        itemListElements={appendDynamicSubcategoryToBreadcrumbSchema(
          [
            {
              position: 1,
              name: placeholder('breadcrumbs.app'),
              item: APP.URL,
            },
            {
              position: 2,
              name: categoryName,
              item: toAbsoluteLink(toCategoryLink(categorySlug)),
            },
            {
              position: 3,
              name: toFormattedJsonLdString(offerTitle),
              item: toAbsoluteLink(toOfferLink(offerSlug)),
            },
          ],
          subcategoryName,
          subcategorySlug
        )}
      />
      <ProductJsonLd
        aggregateOffer={{
          priceCurrency: 'BRL',
          lowPrice: toFormattedOfferPrice(
            lowestPriceOffer.price,
            lowestPriceOffer.priceType
          ),
          highPrice: toFormattedOfferPrice(
            highestPriceOffer.price,
            highestPriceOffer.priceType
          ),
          offerCount,
          offers: [
            {
              price: toFormattedOfferPrice(offerPrice, offerPriceType),
              priceCurrency: 'BRL',
              // TODO: in future we will change it to more dynamic info
              availability: 'InStock',
              itemCondition: 'NewCondition',
              url: storeDomain,
              sellerName: storeName,
              category: categoryName,
              seller: { name: storeName },
            },
            ...offersToCompare.map((offer) => ({
              price: offer.price,
              priceCurrency: 'BRL',
              // TODO: in future we will change it to more dynamic info
              availability: 'InStock',
              itemCondition: 'NewCondition',
              url: offer.redirect,
              sellerName: offer.store.name,
              category: categoryName,
              seller: { name: offer.store.name },
            })),
          ],
        }}
        aggregateRating={
          totalReviews > 0
            ? {
                ratingValue: reviewRating || OFFER.REVIEWS.DEFAULT_RATING_VALUE,
                reviewCount: totalReviews,
              }
            : null
        }
        description={offerInstructionsRaw}
        images={[getOfferImageLink(offerPhoto, 268)]}
        productName={toFormattedJsonLdString(offerTitle)}
        reviews={totalReviews > 0 ? toReviewSchema(reviews, offerTitle) : null}
        sku={offerId}
      />
    </>
  );
};

Schema.propTypes = {
  categoryName: PropTypes.string,
  categorySlug: PropTypes.string.isRequired,
  offerId: PropTypes.number,
  offerInstructionsRaw: PropTypes.string,
  offerOldPrice: PropTypes.number,
  offerPhoto: PropTypes.string,
  offerPrice: PropTypes.number,
  offerPriceType: PropTypes.string.isRequired,
  offerSlug: PropTypes.string,
  offerTitle: PropTypes.string,
  offersToCompare: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      price: PropTypes.number.isRequired,
      redirect: PropTypes.string.isRequired,
      created: PropTypes.string.isRequired,
      store: PropTypes.shape({
        domain: PropTypes.string.isRequired,
        id: PropTypes.number.isRequired,
        image: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        slug: PropTypes.string.isRequired,
      }),
    })
  ),
  reviewRating: PropTypes.number,
  reviews: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      relevance: PropTypes.number.isRequired,
      text: PropTypes.string.isRequired,
      raw: PropTypes.string.isRequired,
      date: PropTypes.string.isRequired,
      likes: PropTypes.number.isRequired,
      statusName: PropTypes.string.isRequired,
      stars: PropTypes.number.isRequired,
      user: PropTypes.shape({
        id: PropTypes.number.isRequired,
        name: PropTypes.string.isRequired,
        username: PropTypes.string.isRequired,
        photo: PropTypes.string.isRequired,
        typeName: PropTypes.string.isRequired,
        level: PropTypes.number.isRequired,
        specialityName: PropTypes.string,
        link: PropTypes.string.isRequired,
      }).isRequired,
    })
  ),
  storeDomain: PropTypes.string,
  storeName: PropTypes.string,
  subcategoryName: PropTypes.string,
  subcategorySlug: PropTypes.string,
  totalReviews: PropTypes.number,
};

export default Schema;
